import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { queryKeys } from '@/lib/query-keys';
import { formatUnknownError } from '@/lib/error-format';

// Types
export interface PendingImport {
  id: string;
  user_id: string;
  client_id: string | null;
  watched_folder_id: string | null;
  drive_file_id: string;
  drive_file_name: string;
  drive_file_mime_type: string | null;
  drive_file_size: number | null;
  drive_file_created_at: string | null;
  drive_file_modified_at: string | null;
  drive_web_view_link: string | null;
  file_type: 'transcript' | 'video' | 'unknown';
  status: 'pending' | 'processing' | 'transcribing' | 'analyzing' | 'completed' | 'failed' | 'dismissed';
  status_message: string | null;
  processed_at: string | null;
  source: 'manual' | 'auto_scan' | 'calendar_link' | 'historical_audit' | null;
  google_calendar_event_id: string | null;
  match_confidence: number | null;
  transcription_job_id: string | null;
  suggested_client_id: string | null;
  suggestion_confidence: number | null;
  suggestion_reason: string | null;
  assigned_client_id: string | null;
  analysis_id: string | null;
  detected_at: string;
  created_at: string;
  // Joined data
  suggested_client?: { id: string; name: string } | null;
  assigned_client?: { id: string; name: string } | null;
  watched_folder?: { id: string; folder_name: string } | null;
}

export interface ScanResult {
  success: boolean;
  folders_scanned: number;
  total_files_found: number;
  total_files_imported: number;
  results?: Array<{
    folder_name: string;
    files_found: number;
    files_imported: number;
    error?: string;
  }>;
}

// Hook to get pending imports with filters
export function usePendingImports(status?: string) {
  return useQuery({
    queryKey: queryKeys.imports.pendingWithStatus(status),
    queryFn: async () => {
      let query = supabase
        .from('pending_imports')
        .select(`
          *,
          suggested_client:suggested_client_id(id, name),
          assigned_client:assigned_client_id(id, name),
          watched_folder:watched_folder_id(id, folder_name)
        `)
        .order('detected_at', { ascending: false });

      if (status) {
        query = query.eq('status', status);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as PendingImport[];
    },
  });
}

// Hook to get count of pending imports (for sidebar badge)
export function usePendingImportsCount() {
  return useQuery({
    queryKey: queryKeys.imports.pendingCount,
    queryFn: async () => {
      const { count, error } = await supabase
        .from('pending_imports')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending');

      if (error) throw error;
      return count || 0;
    },
    refetchInterval: 60000, // Refetch every minute
  });
}

// Hook to scan watched folders
export function useScanWatchedFolders() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (folderId?: string): Promise<ScanResult> => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await supabase.functions.invoke('google-drive-scan', {
        body: folderId ? { action: 'scan-folder', folder_id: folderId } : { action: 'scan-all' },
      });

      if (response.error) {
        throw new Error(response.error.message || 'Scan failed');
      }

      return response.data as ScanResult;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.imports.pending });
      queryClient.invalidateQueries({ queryKey: queryKeys.imports.pendingCount });
      queryClient.invalidateQueries({ queryKey: queryKeys.google.watchedFolders });

      if (data.total_files_imported > 0) {
        toast({
          title: 'Scan complete',
          description: `Found ${data.total_files_imported} new file(s) to import`,
        });
      } else {
        toast({
          title: 'Scan complete',
          description: 'No new files found',
        });
      }
    },
    onError: (error) => {
      toast({
        title: 'Scan failed',
        description: formatUnknownError(error),
        variant: 'destructive',
      });
    },
  });
}

// Hook to assign a client to a pending import
export function useAssignImportClient() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ importId, clientId }: { importId: string; clientId: string }) => {
      const { error } = await supabase
        .from('pending_imports')
        .update({
          assigned_client_id: clientId,
          client_id: clientId, // Also set the main client_id
        })
        .eq('id', importId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.imports.pending });
      toast({ title: 'Client assigned' });
    },
    onError: (error) => {
      toast({
        title: 'Failed to assign client',
        description: formatUnknownError(error),
        variant: 'destructive',
      });
    },
  });
}

// Hook to dismiss a pending import
export function useDismissImport() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (importId: string) => {
      const { error } = await supabase
        .from('pending_imports')
        .update({
          status: 'dismissed',
          dismissed_at: new Date().toISOString(),
        })
        .eq('id', importId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.imports.pending });
      queryClient.invalidateQueries({ queryKey: queryKeys.imports.pendingCount });
      toast({ title: 'Import dismissed' });
    },
    onError: (error) => {
      toast({
        title: 'Failed to dismiss',
        description: formatUnknownError(error),
        variant: 'destructive',
      });
    },
  });
}

// Hook to bulk assign clients
export function useBulkAssignClient() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ importIds, clientId }: { importIds: string[]; clientId: string }) => {
      const { error } = await supabase
        .from('pending_imports')
        .update({
          assigned_client_id: clientId,
          client_id: clientId,
        })
        .in('id', importIds);

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.imports.pending });
      toast({ title: `Assigned ${variables.importIds.length} file(s) to client` });
    },
    onError: (error) => {
      toast({
        title: 'Failed to assign clients',
        description: formatUnknownError(error),
        variant: 'destructive',
      });
    },
  });
}

// Hook to bulk dismiss imports
export function useBulkDismiss() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (importIds: string[]) => {
      const { error } = await supabase
        .from('pending_imports')
        .update({
          status: 'dismissed',
          dismissed_at: new Date().toISOString(),
        })
        .in('id', importIds);

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.imports.pending });
      queryClient.invalidateQueries({ queryKey: queryKeys.imports.pendingCount });
      toast({ title: `Dismissed ${variables.length} file(s)` });
    },
    onError: (error) => {
      toast({
        title: 'Failed to dismiss',
        description: formatUnknownError(error),
        variant: 'destructive',
      });
    },
  });
}

// Hook to import file(s) - downloads and analyzes
export interface ImportResult {
  success: boolean;
  analysis_id?: string;
  client_id?: string;
  video_uploaded?: boolean;
  error?: string;
}

export function useImportFile() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ importId, linkedImportId }: { importId: string; linkedImportId?: string }): Promise<ImportResult> => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await supabase.functions.invoke('google-drive-import', {
        body: { import_id: importId, linked_import_id: linkedImportId },
      });

      if (response.error) {
        throw new Error(response.error.message || 'Import failed');
      }

      // Check if the response data indicates an error
      if (response.data?.error) {
        throw new Error(response.data.error + (response.data.details ? `: ${response.data.details}` : ''));
      }

      return response.data as ImportResult;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.imports.pending });
      queryClient.invalidateQueries({ queryKey: queryKeys.imports.pendingCount });
      queryClient.invalidateQueries({ queryKey: queryKeys.analyses.all });

      toast({
        title: 'Import complete',
        description: data.video_uploaded
          ? 'Transcript analyzed and video uploaded'
          : 'Transcript analyzed successfully',
      });
    },
    onError: (error) => {
      // Invalidate queries to refresh the UI with the failed status
      queryClient.invalidateQueries({ queryKey: queryKeys.imports.pending });
      queryClient.invalidateQueries({ queryKey: queryKeys.imports.pendingCount });

      toast({
        title: 'Import failed',
        description: formatUnknownError(error, 'Unknown error'),
        variant: 'destructive',
      });
    },
  });
}

// Hook to link two files together (transcript + video)
export function useLinkFiles() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ transcriptId, videoId }: { transcriptId: string; videoId: string }) => {
      // Update both files to reference each other via assigned_client_id sync
      // For now, we'll just track them together by assigning the same client
      // The import function will handle them together when both IDs are passed
      return { transcriptId, videoId };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.imports.pending });
      toast({ title: 'Files linked' });
    },
    onError: (error) => {
      toast({
        title: 'Failed to link files',
        description: formatUnknownError(error, 'Unknown error'),
        variant: 'destructive',
      });
    },
  });
}
